import React, { useEffect } from 'react';
import GoogleMapReact from 'google-map-react';
import { connect, useDispatch } from 'react-redux';
import { IconSize, Text2, HeaderText, VdsIcon } from '@el8/vital';
import { Link, RouteComponentProps } from '@reach/router';

import { State } from 'types';
import { AppointmentType } from 'EntityTypes';

import { Box, MapPointer } from 'components';
import { getAppointmentTypeForUrl } from 'modules/appointmentType';
import { fetchServiceLocationCoordinates, useServiceLocation } from 'modules/serviceLocations';
import { usePractice } from 'modules/practice';
import PhysicianAvailabilities from '../PhysicianAvailabilities/PhysicianAvailabilities';
import PracticeHeader from '../Practice/PracticeHeader';

import styles from './ServiceLocation.less';

type OwnProps = {
  practiceId: number;
} & RouteComponentProps<{ appointmentTypeUrl?: string; serviceLocationId?: string }>;

type StateProps = {
  appointmentTypeFromUrl: AppointmentType;
};

type Props = OwnProps & StateProps;

const mapStateToProps = (state: State, { appointmentTypeUrl }: OwnProps): StateProps => {
  return {
    appointmentTypeFromUrl: getAppointmentTypeForUrl(state, { appointmentTypeUrl }),
  };
};

const ServiceLocation = ({
  appointmentTypeFromUrl,
  navigate,
  practiceId,
  serviceLocationId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const [serviceLocation] = useServiceLocation(practiceId, Number(serviceLocationId));
  const [practice] = usePractice(practiceId);
  const { settings } = practice;
  const { showLocationMap } = settings;

  useEffect(() => {
    if (!serviceLocation || !showLocationMap) return;
    if (serviceLocation.lat && serviceLocation.lng) return;
    dispatch(fetchServiceLocationCoordinates(serviceLocation));
  }, [dispatch, serviceLocation, showLocationMap]);

  if (!serviceLocation) return null;

  return (
    <Box align="top">
      <div className={styles.mainInfo}>
        <PracticeHeader practiceId={practiceId} appointmentTypeFromUrl={appointmentTypeFromUrl} />
        <div>
          <HeaderText
            className={styles.locationName}
            legacyVariant="legacyTitleText"
            sizing="heading-lg"
            tag="h1"
          >
            {serviceLocation.name}
          </HeaderText>
          <Text2 sizing="sm">{serviceLocation.fullAddress}</Text2>
          <Link className={styles.backLink} to={`/book/${practiceId}`}>
            <VdsIcon icon="back" size={IconSize.XXSmall} /> Back to practice locations
          </Link>
        </div>
        <PhysicianAvailabilities
          navigate={navigate}
          practiceId={practiceId}
          serviceLocationId={Number(serviceLocationId)}
        />
      </div>
      {!!(serviceLocation.lat && serviceLocation.lng && showLocationMap) && (
        <div className={styles.map}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: window.el8Globals.GOOGLE_MAPS_API_KEY }}
            defaultCenter={{ lat: serviceLocation.lat, lng: serviceLocation.lng }}
            defaultZoom={13}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
            }}
          >
            <MapPointer lat={serviceLocation.lat} lng={serviceLocation.lng} />
          </GoogleMapReact>
        </div>
      )}
    </Box>
  );
};

export default connect(mapStateToProps)(ServiceLocation);
